import type { CSSProperties } from "react";

/**
 * Sequential **page** build = blocks enter top-to-bottom on first visit (blur-in-up or
 * light meso), not a single fade on the whole route. See tracker §4g for full coverage.
 *
 * - Statement-gated full builds: `setup-method`, `complete` (not in this set).
 * - Peak page build: `BundleAnalysisRevealStep` intro blocks (§4a) + `bundle-split-intro` (§4d).
 */

/** Steps that run a mount build on first visit (per session). */
export const STEP_SEQUENTIAL_BUILD_STEPS = new Set([
  "landing",
  "lead-details",
  "goals",
  "unavailable",
  "register",
  "two-factor",
  "business",
  "employees",
  "plans",
  "ancillary",
  "schedule",
]);

/**
 * Diminishing ceremony (MOTION-PRINCIPLES §2.5): valley steps — one short meso beat
 * on mount. Business keeps full stagger (form fields); open/peak/close are separate.
 */
export const STEP_SEQUENTIAL_BUILD_LIGHT_STEPS = new Set([
  "landing",
  "lead-details",
  "goals",
  "unavailable",
  "register",
  "two-factor",
  "plans",
  "ancillary",
]);

export type StepBuildCeremony = "full" | "light";

/** First block only on light steps — avoids wall-to-wall stagger. */
export const STEP_BUILD_LIGHT_MAX_DELAY_S = 0.1;

export function getStepBuildCeremony(step: string): StepBuildCeremony | null {
  if (!STEP_SEQUENTIAL_BUILD_STEPS.has(step)) return null;
  return STEP_SEQUENTIAL_BUILD_LIGHT_STEPS.has(step) ? "light" : "full";
}

export function shouldAnimateBuildBlock(
  delayS: number,
  ceremony: StepBuildCeremony | null,
): boolean {
  if (!ceremony) return false;
  if (ceremony === "full") return true;
  return delayS <= STEP_BUILD_LIGHT_MAX_DELAY_S;
}

function scaleBuildDelay(delayS: number, ceremony: StepBuildCeremony): number {
  return ceremony === "light" ? delayS * 0.4 : delayS;
}

export type StepBuildMotionProps = { className: string; style: CSSProperties } | undefined;

export function stepBuildProps(
  playBuild: boolean,
  delayS: number,
  ceremony: StepBuildCeremony | null = "full",
): StepBuildMotionProps {
  if (!playBuild || !ceremony || !shouldAnimateBuildBlock(delayS, ceremony)) {
    return undefined;
  }

  const scaledDelay = scaleBuildDelay(delayS, ceremony);

  if (ceremony === "light") {
    return {
      className: "bd-v2-step-build-light",
      style: { animationDelay: `${scaledDelay}s` },
    };
  }

  return {
    className: "cw-blur-in-up",
    style: { animationDelay: `${scaledDelay}s` },
  };
}

/**
 * Page/step titles and intro lines (eyebrow, h1, description) — always `cw-blur-in-up` on first
 * visit. Light ceremony applies only to body blocks via `stepBuildProps`, not page intros.
 */
export function stepTitleBuildProps(
  playBuild: boolean,
  ceremony: StepBuildCeremony | null,
  delayS = 0,
): StepBuildMotionProps {
  if (!playBuild || !ceremony) {
    return undefined;
  }

  const scaledDelay = scaleBuildDelay(delayS, ceremony);

  return {
    className: "cw-blur-in-up",
    style: { animationDelay: `${scaledDelay}s` },
  };
}

const PAGE_INTRO_TITLE_OFFSET_S = 0.07;
const PAGE_INTRO_DESCRIPTION_OFFSET_S = 0.13;

/** Stagger rhythm for multi-block pages (§4 brand tokens: ~0.15 base, ~0.07 step). */
export const PAGE_BODY_BLOCK_BASE_DELAY_S = 0.12;
export const PAGE_BODY_BLOCK_STAGGER_S = 0.07;

export function pageBodyBlockBuildProps(
  playBuild: boolean,
  blockIndex: number,
  ceremony: StepBuildCeremony | null = "full",
): StepBuildMotionProps {
  return stepBuildProps(
    playBuild,
    PAGE_BODY_BLOCK_BASE_DELAY_S + blockIndex * PAGE_BODY_BLOCK_STAGGER_S,
    ceremony,
  );
}

/**
 * Merge build motion with layout `className` / `style`. Always use this when the element also
 * has `className={cn(...)}` — a later className prop overwrites `{...stepBuildProps()}` and drops
 * `cw-blur-in-up`.
 */
export function mergeStepBuildProps(
  motion: StepBuildMotionProps,
  options?: { className?: string; style?: CSSProperties },
): { className?: string; style?: CSSProperties } {
  if (!motion) {
    return options ?? {};
  }

  return {
    className: options?.className ? `${motion.className} ${options.className}` : motion.className,
    style: options?.style ? { ...motion.style, ...options.style } : motion.style,
  };
}

/**
 * Eyebrow → title → description stagger (`cw-blur-in-up` on all steps; scaled delay on light steps).
 * Consumers: spread into `SectionTitle` / `StepIntro`. Motion wrappers must merge classes with
 * `cn(motion.className, …)` — do not pass `className` after `{...motion}` or `cw-blur-in-up` is dropped.
 */
export function sectionPageIntroMotions(
  playBuild: boolean,
  ceremony: StepBuildCeremony | null,
  baseDelayS = 0,
): {
  eyebrowMotion?: StepBuildMotionProps;
  titleMotion?: StepBuildMotionProps;
  descriptionMotion?: StepBuildMotionProps;
} {
  if (!playBuild || !ceremony) {
    return {};
  }

  return {
    eyebrowMotion: stepTitleBuildProps(playBuild, ceremony, baseDelayS),
    titleMotion: stepTitleBuildProps(playBuild, ceremony, baseDelayS + PAGE_INTRO_TITLE_OFFSET_S),
    descriptionMotion: stepTitleBuildProps(
      playBuild,
      ceremony,
      baseDelayS + PAGE_INTRO_DESCRIPTION_OFFSET_S,
    ),
  };
}
